import FadeUp from "./FadeUp";
import { TrendingDown, DollarSign, Search, Megaphone, Store } from "lucide-react";

const pains = [
    { icon: DollarSign, text: "Paid ads keep getting more expensive." },
    { icon: Search, text: "SEO takes months, and the SERP is a bloodbath." },
    { icon: Megaphone, text: "You launch a big feature and it quietly fades away because you can't get it in front of the right users." },
    { icon: Store, text: "SaaS marketplaces used to be a great referral source five years back. Now they mostly recommend whoever pays them the most." },
];

export const Problem = () => (
    <section
        id="problem"
        data-testid="problem-section"
        className="py-24 md:py-32 bg-white"
    >
        <div className="container-page">
            <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
                <div>
                    <FadeUp>
                        <p className="text-xs font-bold tracking-[0.22em] uppercase text-gray-500">
                            The problem
                        </p>
                    </FadeUp>
                    <FadeUp delay={0.05}>
                        <h2
                            data-testid="problem-heading"
                            className="mt-4 font-display font-extrabold text-3xl md:text-4xl lg:text-[44px] tracking-[-0.02em] leading-[1.55] md:leading-[1.55] lg:leading-[1.55] text-[#0A0A0A] text-balance"
                        >
                            Most productivity apps rely on channels that stop working the moment you stop paying.
                        </h2>
                    </FadeUp>

                    <div className="mt-10 space-y-3">
                        {pains.map((p, i) => (
                            <FadeUp key={i} delay={0.05 + i * 0.05}>
                                <div
                                    data-testid={`problem-pain-${i}`}
                                    className="flex items-start gap-4 rounded-2xl border border-black/[0.08] bg-white p-4 md:p-5"
                                >
                                    <div className="flex-none h-10 w-10 rounded-xl bg-[#FAFAFA] border border-black/[0.06] flex items-center justify-center">
                                        <p.icon className="h-4.5 w-4.5 text-[#0A0A0A]" />
                                    </div>
                                    <p className="text-[15px] md:text-base text-gray-700 leading-relaxed pt-1.5">
                                        {p.text}
                                    </p>
                                </div>
                            </FadeUp>
                        ))}
                    </div>

                    <FadeUp delay={0.3}>
                        <div className="mt-8 rounded-2xl bg-[#0A0A0A] text-white p-6 md:p-7">
                            <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-[#C6F432]">
                                The missing asset
                            </p>
                            <p className="mt-3 font-display font-bold text-xl md:text-2xl tracking-tight leading-[1.3]">
                                A creator ecosystem that compounds.
                            </p>
                        </div>
                    </FadeUp>
                </div>

                {/* Right: plateau visual */}
                <FadeUp delay={0.15}>
                    <div
                        data-testid="problem-visual"
                        className="relative rounded-3xl border border-black/[0.08] bg-[#FAFAFA] p-6 md:p-8"
                    >
                        <div className="flex items-center justify-between">
                            <span className="text-[11px] font-bold tracking-[0.22em] uppercase text-gray-500">
                                Growth curves
                            </span>
                            <TrendingDown className="h-4 w-4 text-gray-400" />
                        </div>
                        <div className="mt-4 relative h-64 md:h-72 w-full">
                            <svg viewBox="0 0 400 260" className="w-full h-full" preserveAspectRatio="none">
                                {[0, 1, 2, 3, 4].map((i) => (
                                    <line
                                        key={i}
                                        x1="0"
                                        x2="400"
                                        y1={40 + i * 45}
                                        y2={40 + i * 45}
                                        stroke="rgba(0,0,0,0.05)"
                                        strokeWidth="1"
                                    />
                                ))}
                                <path
                                    d="M 10 220 Q 90 90, 180 90 Q 260 90, 320 130 Q 360 155, 390 175"
                                    fill="none"
                                    stroke="#9CA3AF"
                                    strokeWidth="2.5"
                                    strokeDasharray="6 5"
                                />
                                <path
                                    d="M 10 235 Q 100 220, 190 180 Q 270 155, 390 150"
                                    fill="none"
                                    stroke="#9CA3AF"
                                    strokeWidth="2.5"
                                />
                                <path
                                    d="M 10 230 Q 80 218, 150 195 Q 220 170, 280 120 T 390 30"
                                    fill="none"
                                    stroke="#0A0A0A"
                                    strokeWidth="3"
                                />
                                <circle cx="390" cy="30" r="6" fill="#C6F432" stroke="#0A0A0A" strokeWidth="2" />
                            </svg>
                        </div>
                        <div className="mt-5 grid grid-cols-3 gap-3 text-[12px]">
                            <LegendDot color="#9CA3AF" dashed label="Paid ads" />
                            <LegendDot color="#9CA3AF" label="SEO" />
                            <LegendDot color="#0A0A0A" bold label="Creator engine" />
                        </div>
                        <p className="mt-4 text-[13px] text-gray-500 leading-relaxed">
                            The only line that keeps rising after you stop spending.
                        </p>
                    </div>
                </FadeUp>
            </div>
        </div>
    </section>
);

const LegendDot = ({ color, label, dashed, bold }) => (
    <div className="flex items-center gap-2">
        <span
            className="inline-block h-[2px] w-6"
            style={{
                backgroundImage: dashed
                    ? `repeating-linear-gradient(to right, ${color} 0 4px, transparent 4px 8px)`
                    : "none",
                backgroundColor: dashed ? "transparent" : color,
                height: bold ? 3 : 2,
            }}
        />
        <span className={bold ? "text-[#0A0A0A] font-semibold" : "text-gray-500"}>
            {label}
        </span>
    </div>
);

export default Problem;
